import React from 'react';
import {
  DashboardOutlined,
  ThunderboltOutlined,
  SettingOutlined,
  CarOutlined,
  BgColorsOutlined,
  TeamOutlined,
  BorderOutlined,
} from '@ant-design/icons';
import type { ListingDetailsData } from '../hooks/useListingDetails';

interface SpecItem {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
}

interface SpecsGridProps {
  listing: Pick<
    ListingDetailsData,
    | 'mileage' | 'fuelType' | 'transmission' | 'bodyType'
    | 'color' | 'doors' | 'seats'
    | 'isImported' | 'importCountry' | 'shippingIncluded'
    | 'importAvailability' | 'estimatedArrivalDate'
  >;
}


const labelMap: Record<string, string> = {
  automatic: 'Automatic',
  manual: 'Manual',
  semi_automatic: 'Semi-Auto',
  cvt: 'CVT',
  petrol: 'Petrol',
  diesel: 'Diesel',
  electric: 'Electric',
  hybrid: 'Hybrid',
  plugin_hybrid: 'Plug-in Hybrid',
  lpg: 'LPG',
  new: 'New',
  used: 'Used',
  certified: 'Certified Pre-Owned',
  sedan: 'Sedan',
  suv: 'SUV',
  hatchback: 'Hatchback',
  coupe: 'Coupé',
  convertible: 'Convertible',
  van: 'Van',
  truck: 'Truck',
  wagon: 'Wagon',
  pickup: 'Pickup',
  minivan: 'Minivan',
};

const humanize = (val: string | null | undefined): string | null => {
  if (!val) return null;
  return (
    labelMap[val.toLowerCase()] ||
    val.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

const SpecsGrid: React.FC<SpecsGridProps> = ({ listing }) => {
  const specs: SpecItem[] = [
    {
      icon: <DashboardOutlined />,
      label: 'Mileage',
      value: listing.mileage ? `${listing.mileage.toLocaleString()} km` : null,
    },
    {
      icon: <ThunderboltOutlined />,
      label: 'Fuel Type',
      value: humanize(listing.fuelType),
    },
    {
      icon: <SettingOutlined />,
      label: 'Transmission',
      value: humanize(listing.transmission),
    },
    {
      icon: <CarOutlined />,
      label: 'Body Type',
      value: humanize(listing.bodyType),
    },
    {
      icon: <BgColorsOutlined />,
      label: 'Color',
      value: humanize(listing.color),
    },
    {
      icon: <BorderOutlined />,
      label: 'Doors',
      value: listing.doors ? `${listing.doors} doors` : null,
    },
    {
      icon: <TeamOutlined />,
      label: 'Seats',
      value: listing.seats ? `${listing.seats} seats` : null,
    },
  ].filter((s) => s.value != null);

  if (specs.length === 0) return null;

  return (
    <div className="specs-grid">
      {specs.map((spec) => (
        <div key={spec.label} className="spec-card">
          <div className="spec-card-icon">{spec.icon}</div>
          <div className="spec-card-body">
            <span className="spec-card-label">{spec.label}</span>
            <span className="spec-card-value">{spec.value}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SpecsGrid;
